import React, { FC, memo, useCallback, useMemo } from "react";
import { dateFormat, IconButton } from "@evergis/ui";

import { FeatureCardChip, HiddenFilters } from "../styled";
import { transparentizeColor } from "../../../utils/color";
import { isVisibleContainer } from "../utils/isVisibleContainer";
import { getFilterSelectedItems } from "../utils/getFilterSelectedItems";
import { getConfigFilter } from "../utils/getConfigFilter";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { ConfigFilterValueType, ContainerProps } from "../types";

const spliceValue = (filterValue: string[], splicingValue: string) => {
  const newValue = [...filterValue];

  newValue.splice(newValue.indexOf(splicingValue), 1);

  return newValue;
};

export const HiddenTitleItems: FC<ContainerProps & { filter?: string }> = memo(
  ({ elementConfig, config, type, filter }) => {
    const { filters, dataSources, changeFilters, expandedContainers } = useWidgetContext(type);
    const { filters: configFilters } = config || {};
    const { id, options, children } = elementConfig || {};
    const { fontColor, expandable, expanded } = options || {};

    const isVisible = isVisibleContainer(id, expandable, expanded, expandedContainers);

    const filterItems = useMemo(() => children?.filter(child => child.options?.filterName), [children]);

    const configFilter = useMemo(() => getConfigFilter(filter, configFilters), [configFilters, filter]);

    const clearFilter = useCallback(
      (name: string, value?: string) => {
        changeFilters(
          {
            [name]: {
              value:
                value &&
                filters[name]?.value &&
                Array.isArray(filters[name].value) &&
                configFilter?.valueType === "array"
                  ? spliceValue(filters[name].value as string[], value)
                  : "",
            },
          },
          configFilter?.resetFilters,
        );
      },
      [changeFilters, configFilter?.resetFilters, configFilter?.valueType, filters],
    );

    const selectedItems = useMemo(
      () => getFilterSelectedItems(filterItems, filters, configFilters),
      [configFilters, filters, filterItems],
    );

    const isLoading = useMemo(
      () =>
        elementConfig?.children?.some(child => {
          const currentFilter = configFilters?.find(({ name }) => name === child.options?.filterName);

          return (
            !!currentFilter &&
            !!child.options?.filterName &&
            !dataSources?.some(({ name }) => name === currentFilter.relatedDataSource)
          );
        }),
      [configFilters, dataSources, elementConfig?.children],
    );

    const chipProps = useMemo(
      () => ({
        $fontSize: "0.75rem",
        $fontColor: fontColor,
        $bgColor: fontColor ? transparentizeColor(fontColor, 6) : null,
        $radius: "0.25rem",
      }),
      [fontColor],
    );

    const formatValue = useCallback((value: string | Date) => {
      return typeof value === "object" ? dateFormat(value) : value;
    }, []);

    const formatJoin = useCallback((valueType: ConfigFilterValueType) => {
      return valueType === "range" ? " - " : ", ";
    }, []);

    const renderItem = useCallback(
      (filterName: string, value: string, index: number) => (
        <FeatureCardChip key={index} {...chipProps}>
          {value} <IconButton kind="close" onClick={() => clearFilter(filterName, value)} />
        </FeatureCardChip>
      ),
      [chipProps, clearFilter],
    );

    if (isLoading || isVisible || !selectedItems.length) return null;

    return (
      <HiddenFilters>
        {filter && Array.isArray(filters[filter]?.value)
          ? renderItem(
              filter,
              (filters[filter].value as string[]).map(formatValue).join(formatJoin(configFilter?.valueType)),
              0,
            )
          : selectedItems
              .filter(
                ({ options: { filterName } }, index) =>
                  index === selectedItems.findIndex(({ options: { filterName: name } }) => name === filterName),
              )
              .map(({ options: { filterName } }, index) =>
                renderItem(
                  filterName,
                  Array.isArray(filters[filterName]?.value)
                    ? (filters[filterName].value as string[])
                        .map(formatValue)
                        .join(formatJoin(getConfigFilter(filterName, configFilters)?.valueType))
                    : (filters[filterName]?.value as string) ||
                        (getConfigFilter(filterName, configFilters)?.defaultValue as string),
                  index,
                ),
              )}
      </HiddenFilters>
    );
  },
);
